import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Landmark,
  PhoneIncoming,
  Megaphone,
  Headset,
  Users,
  TrendingUp,
  Globe,
  BarChart3,
  ChevronRight,
} from "lucide-react";
import { SectionHeader } from "@/components/Reveal";

const DEPTS = [
  { key: "finance", label: "Finance AI", icon: Landmark },
  { key: "reception", label: "Receptionist AI", icon: PhoneIncoming },
  { key: "marketing", label: "Marketing AI", icon: Megaphone },
  { key: "support", label: "Support AI", icon: Headset },
  { key: "hr", label: "HR AI", icon: Users },
  { key: "sales", label: "Sales AI", icon: TrendingUp },
  { key: "website", label: "Website AI", icon: Globe },
  { key: "analytics", label: "Analytics AI", icon: BarChart3 },
];

const RULES = [
  { dept: "finance", keys: ["invoice", "finance", "payroll", "cashflow", "expense", "vat", "tax"], response: "FINANCE AI ACTIVATED — Drafting invoices · Syncing with Xero · Scheduling payment reminders" },
  { dept: "reception", keys: ["call", "answer", "phone", "reception", "appointment", "book"], response: "RECEPTIONIST AI ACTIVATED — Call schedule locked · Greetings loaded · Routing rules armed" },
  { dept: "marketing", keys: ["campaign", "facebook", "ads", "marketing", "social", "content", "seo", "blog"], response: "MARKETING AI ACTIVATED — Audiences built · Creatives drafted · Campaign queued for launch" },
  { dept: "support", keys: ["support", "ticket", "chat", "whatsapp", "customer", "enquir"], response: "SUPPORT AI ACTIVATED — Channels opened · Knowledge base loaded · Response engine live" },
  { dept: "hr", keys: ["hire", "recruit", "onboard", "hr", "training", "staff"], response: "HR AI ACTIVATED — Pipeline opened · Onboarding flows ready · Documents prepared" },
  { dept: "sales", keys: ["lead", "sales", "quote", "proposal", "follow", "deal"], response: "SALES AI ACTIVATED — Leads scored · Proposals drafted · Follow-ups scheduled" },
  { dept: "website", keys: ["website", "landing", "page", "ecommerce", "store", "site"], response: "WEBSITE AI ACTIVATED — Structure mapped · Copy drafted · Deployment pipeline ready" },
  { dept: "analytics", keys: ["report", "analytics", "dashboard", "insight", "data", "forecast"], response: "ANALYTICS AI ACTIVATED — Data aggregated · Trends detected · Report rendering" },
];

const EXAMPLES = [
  { text: "Create invoices", dept: "finance" },
  { text: "Answer tomorrow's calls", dept: "reception" },
  { text: "Create Facebook campaign", dept: "marketing" },
];

const match = (text) => {
  const t = text.toLowerCase();
  for (const r of RULES) {
    if (r.keys.some((k) => t.includes(k))) return r;
  }
  return null;
};

export default function CommandCenter() {
  const [value, setValue] = useState("");
  const [activeDept, setActiveDept] = useState(null);
  const [response, setResponse] = useState("");
  const [interacted, setInteracted] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (interacted) return undefined;
    let i = 0;
    let char = 0;
    let deleting = false;
    let timer;
    const step = () => {
      const ex = EXAMPLES[i];
      if (!deleting) {
        char += 1;
        setValue(ex.text.slice(0, char));
        if (char === ex.text.length) {
          const r = match(ex.text);
          setActiveDept(ex.dept);
          setResponse(r ? r.response : "");
          deleting = true;
          timer = setTimeout(step, 2400);
          return;
        }
        timer = setTimeout(step, 60);
      } else {
        char -= 1;
        setValue(ex.text.slice(0, char));
        if (char === 0) {
          deleting = false;
          setActiveDept(null);
          setResponse("");
          i = (i + 1) % EXAMPLES.length;
          timer = setTimeout(step, 600);
          return;
        }
        timer = setTimeout(step, 26);
      }
    };
    timer = setTimeout(step, 1200);
    return () => clearTimeout(timer);
  }, [interacted]);

  const onChange = (v) => {
    if (!interacted) setInteracted(true);
    setValue(v);
    const r = match(v);
    if (r && v.trim().length > 2) {
      setActiveDept(r.dept);
      setResponse(r.response);
    } else {
      setActiveDept(null);
      setResponse("");
    }
  };

  return (
    <section
      id="demo"
      data-testid="command-center-section"
      className="relative overflow-hidden py-32"
    >
      <div className="grid-lines absolute inset-0 opacity-60 [mask-image:radial-gradient(ellipse_60%_60%_at_50%_50%,black,transparent)]" />
      <div className="absolute left-1/2 top-1/2 h-[500px] w-[800px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(ellipse_at_center,rgba(74,222,128,0.05),transparent_65%)] blur-3xl" />

      <div className="relative mx-auto max-w-5xl px-6 lg:px-10">
        <SectionHeader
          align="center"
          label="Interactive Demo"
          title={
            <>
              Command Your <span className="gold-text">Workforce.</span>
            </>
          }
          sub="Type an instruction. Watch the right AI department activate instantly."
        />

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="glass-panel mt-16 overflow-hidden rounded-2xl border-gold/15 shadow-[0_40px_120px_-40px_rgba(216,192,138,0.3)]"
          data-testid="command-terminal"
        >
          <div className="flex items-center justify-between border-b border-white/5 px-6 py-4">
            <div className="flex items-center gap-2.5">
              <span className="h-2.5 w-2.5 rounded-full bg-white/10" />
              <span className="h-2.5 w-2.5 rounded-full bg-white/10" />
              <span className="h-2.5 w-2.5 rounded-full bg-gold/50" />
            </div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold/80">
              Alkyrvis Command Centre
            </p>
            <span className="status-dot" />
          </div>

          <div className="px-6 py-8 md:px-10">
            <div
              className="flex items-center gap-3 rounded-xl border border-white/10 bg-black/50 px-5 py-4 transition-[border-color,box-shadow] duration-300 focus-within:border-gold/50 focus-within:shadow-[0_0_30px_rgba(216,192,138,0.15)]"
              onClick={() => inputRef.current && inputRef.current.focus()}
            >
              <ChevronRight className="h-5 w-5 shrink-0 text-gold" />
              <div className="relative w-full">
                <input
                  ref={inputRef}
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                  placeholder="Try: Create invoices"
                  data-testid="command-input"
                  className="w-full bg-transparent font-mono text-sm text-[#F5F5F5] outline-none placeholder:text-white/20 md:text-base"
                />
                {!interacted && (
                  <span className="caret pointer-events-none absolute inset-y-0 left-0" style={{ width: 0 }} />
                )}
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-2.5">
              {EXAMPLES.map((ex) => (
                <button
                  key={ex.text}
                  type="button"
                  data-testid={`command-chip-${ex.dept}`}
                  onClick={() => onChange(ex.text)}
                  className="rounded-full border border-white/10 px-4 py-1.5 font-mono text-[11px] text-[#A3A3A3] transition-[border-color,color,background-color] duration-300 hover:border-gold/40 hover:bg-gold/10 hover:text-gold"
                >
                  {ex.text}
                </button>
              ))}
            </div>

            <div className="mt-5 flex items-center gap-4">
              <span className="h-px flex-1 bg-white/5" />
              <button
                type="button"
                data-testid="command-concierge-link"
                onClick={() => window.dispatchEvent(new CustomEvent("open-concierge"))}
                className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A3A3A3] transition-colors duration-300 hover:text-gold"
              >
                Same intelligence, conversation style — ask the Concierge
              </button>
              <span className="h-px flex-1 bg-white/5" />
            </div>

            <div className="mt-6 min-h-[28px]">
              <AnimatePresence mode="wait">
                {response && (
                  <motion.p
                    key={response}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.4 }}
                    className="font-mono text-xs leading-relaxed text-mint md:text-sm"
                    data-testid="command-response"
                  >
                    {response}
                  </motion.p>
                )}
              </AnimatePresence>
            </div>

            <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {DEPTS.map((d) => {
                const active = activeDept === d.key;
                return (
                  <div
                    key={d.key}
                    data-testid={`dept-node-${d.key}`}
                    className={`relative flex flex-col items-center gap-2.5 rounded-xl border px-3 py-5 transition-[border-color,background-color,box-shadow,transform] duration-500 ${
                      active
                        ? "scale-[1.05] border-mint/60 bg-mint/[0.07] shadow-[0_0_40px_rgba(74,222,128,0.25)]"
                        : "border-white/5 bg-white/[0.02]"
                    }`}
                  >
                    <d.icon
                      className={`h-5 w-5 transition-colors duration-500 ${
                        active ? "text-mint" : "text-[#A3A3A3]"
                      }`}
                    />
                    <span
                      className={`text-center font-mono text-[10px] uppercase tracking-[0.15em] transition-colors duration-500 ${
                        active ? "text-mint" : "text-[#A3A3A3]"
                      }`}
                    >
                      {d.label}
                    </span>
                    <span
                      className={`h-1.5 w-1.5 rounded-full transition-[background-color,box-shadow] duration-500 ${
                        active
                          ? "bg-mint shadow-[0_0_10px_rgba(74,222,128,0.9)]"
                          : "bg-white/15"
                      }`}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
